import { useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  CAMPO_GASTO_LABELS,
  categoriasPorProveedor,
  detectarPlantilla,
  detectDayFirst,
  diaPrimeroEnPais,
  entenderColumnas,
  fechaDeCelda,
  EXPENSE_EXAMPLE_FILE,
  fechasAmbiguas,
  filasDeExcel,
  filasDePlantillaGastos,
  looksLikeHeader,
  motivoPorConfirmar,
  MENSAJE_EXTRACTO_BANCO,
  normalizeText,
  pareceExtractoBancario,
  porQueNoEntraNada,
  PLANTILLA_ARCHIVOS,
  revisarGastos,
  SHARED_LOCATION_ID,
  suggestUsefulLife,
  type CorreccionesGastos,
  type DestinoColumna,
  type FilaGasto,
  type PlantillaDetectada,
} from '@alexiarest/shared';
import {
  ImportExpensesError,
  useImportExpenses,
  useImportMappings,
  useMe,
  useSaveImportMapping,
  useSettings,
  useSuppliers,
} from '../api/hooks';
import { useLocationScope } from '../context/LocationContext';
import { api } from '../api/client';
import { descargarArchivoPublico, readImportFileRaw } from '../importFile';
import { formatMoney, formatNumber, today } from '../format';
import { SmartSelect } from './Field';
import { Drawer } from './Drawer';
import { GastoExcelRevisar } from './GastoExcelRevisar';
import { useToast } from './Toast';
import '../pages/gastos.css';

/**
 * Importar gastos (rediseño de Alex, 11 oct 2026; bloque G, 14 oct 2026).
 *
 * Dos caminos (GastoExcelElegir): «Ya tengo mis gastos en un Excel» —el mío,
 * el del contador o el del banco, se sube TAL CUAL— y la plantilla para quien
 * no tiene nada. Los dos acaban en «Antes de guardar» (GastoExcelRevisar): un
 * solo botón, «Guardar N gastos»; los dudosos no bloquean, van a «Por
 * confirmar».
 */

interface Archivo {
  name: string;
  raw: string[][];
  det?: PlantillaDetectada;
  /** Índice de la cabecera en `raw` (-1 = sin cabecera). */
  cabecera: number;
  cabeceras: string[];
  datos: string[][];
}

const MAX_ROWS = 2_000;

function letra(index: number): string {
  let n = index;
  let out = '';
  do {
    out = String.fromCharCode(65 + (n % 26)) + out;
    n = Math.floor(n / 26) - 1;
  } while (n >= 0);
  return out;
}

export function ImportExpensesWizard({
  open,
  onClose,
  onFoto,
}: {
  open: boolean;
  onClose: () => void;
  /** «Mejor súbelas como foto»: abre el escáner. */
  onFoto?: () => void;
}): JSX.Element | null {
  if (!open) return null;
  return <Asistente onClose={onClose} onFoto={onFoto} />;
}

function Asistente({ onClose, onFoto }: { onClose: () => void; onFoto?: () => void }): JSX.Element {
  const toast = useToast();
  const me = useMe();
  const suppliers = useSuppliers();
  const settings = useSettings();
  const { locations, locationId, writeLocationId } = useLocationScope();
  const activos = locations.filter((l) => l.active);
  const importar = useImportExpenses();
  const mappings = useImportMappings('gastos');
  const saveMapping = useSaveImportMapping('gastos');
  const inputRef = useRef<HTMLInputElement>(null);

  const [archivo, setArchivo] = useState<Archivo | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [leyendo, setLeyendo] = useState(false);
  const [columnas, setColumnas] = useState<DestinoColumna[]>([]);
  const [ignoradas, setIgnoradas] = useState<number[]>([]);
  const [faltan, setFaltan] = useState<Array<'fecha' | 'total'>>([]);
  const [correcciones, setCorrecciones] = useState<CorreccionesGastos>({});
  const [diaPrimeroElegido, setDiaPrimeroElegido] = useState<boolean | null>(null);
  const [destino, setDestino] = useState<string>(locationId ?? writeLocationId);
  const [fallo, setFallo] = useState<{ mensaje: string; fila?: number } | null>(null);

  const onPickFile = async (picked: File | undefined) => {
    if (!picked) return;
    setError(null);
    setFallo(null);
    setLeyendo(true);
    setCorrecciones({});
    setDiaPrimeroElegido(null);
    try {
      const raw = await readImportFileRaw(picked, 'Gastos');
      const det = detectarPlantilla(raw);
      if (det && det.kind !== 'gastos') {
        setError(det.kind === 'ingredientes'
          ? 'Este es el Excel de ingredientes. Súbelo en Ingredientes › Excel.'
          : 'Este es el Excel de recetas. Súbelo en Tu carta › Excel.');
        setArchivo(null);
        return;
      }
      if (det) {
        const filas = filasDePlantillaGastos(raw, det);
        if (filas.length === 0) {
          setError('La plantilla está vacía: escribe tus gastos debajo de la línea verde y vuelve a subirla. Los ejemplos en gris no se suben.');
          setArchivo(null);
          return;
        }
        if (filas.length > MAX_ROWS) {
          setError(`La plantilla tiene más de ${formatNumber(MAX_ROWS)} gastos. Pártela (por meses, por ejemplo) y sube cada parte.`);
          setArchivo(null);
          return;
        }
        setArchivo({ name: picked.name, raw, det, cabecera: det.cabecera, cabeceras: [], datos: [] });
        setColumnas([]);
        setFaltan([]);
        return;
      }
      // Cualquier otro Excel: la cabecera es la primera fila con dos celdas o
      // más que no son datos (el del banco trae el nombre del titular arriba).
      let cabecera = -1;
      for (let i = 0; i < Math.min(raw.length, 15); i += 1) {
        const fila = raw[i] ?? [];
        if (fila.filter((c) => String(c ?? '').trim()).length >= 2) {
          cabecera = looksLikeHeader(fila) ? i : -1;
          break;
        }
      }
      const datos = raw.slice(cabecera + 1).filter((r) => r.some((c) => String(c ?? '').trim() !== ''));
      if (datos.length === 0) {
        setError('El archivo está vacío o solo tiene la cabecera. Una fila por gasto, por favor.');
        setArchivo(null);
        return;
      }
      if (datos.length > MAX_ROWS) {
        setError(`El archivo tiene más de ${formatNumber(MAX_ROWS)} filas. Pártelo (por meses, por ejemplo) y sube cada parte.`);
        setArchivo(null);
        return;
      }
      const ancho = Math.max(...raw.slice(Math.max(0, cabecera)).map((r) => r.length));
      const cabeceras = Array.from({ length: ancho }, (_, i) => (cabecera >= 0 ? String(raw[cabecera][i] ?? '').trim() : '') || letra(i));
      // Un extracto del banco no se lee (decidido con Alex, 14 oct 2026).
      if (pareceExtractoBancario(cabeceras, datos)) {
        setError(MENSAJE_EXTRACTO_BANCO);
        setArchivo(null);
        return;
      }
      const entendidas = entenderColumnas(cabeceras, datos.slice(0, 20));
      // Lo que se emparejó a mano la última vez con este mismo formato, manda.
      const guardado = (mappings.data ?? []).find((m) => m.headers.length === cabeceras.length && m.headers.every((h, i) => normalizeText(h) === normalizeText(cabeceras[i])));
      const cols = guardado
        ? cabeceras.map((h, i) => {
            const k = (guardado.columns[normalizeText(h)] ?? guardado.columns[h]) as DestinoColumna | undefined;
            return k && (k in CAMPO_GASTO_LABELS) ? k : entendidas.columnas[i];
          })
        : entendidas.columnas;
      setArchivo({ name: picked.name, raw, cabecera, cabeceras, datos });
      setColumnas(cols);
      setIgnoradas(entendidas.ignoradas);
      setFaltan([
        ...(!cols.includes('fecha') ? ['fecha' as const] : []),
        ...(!cols.includes('total') && !cols.includes('base') ? ['total' as const] : []),
      ]);
    } catch {
      setError('No se pudo leer el archivo. Comprueba que es un Excel (.xlsx) o un CSV sin contraseña.');
      setArchivo(null);
    } finally {
      setLeyendo(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const cambiarColumna = (i: number, destinoCol: DestinoColumna) =>
    setColumnas((prev) => {
      const next = [...prev];
      // Solo el IVA puede estar en varias columnas.
      if (destinoCol !== 'ignorar' && destinoCol !== 'iva') next.forEach((c, j) => { if (c === destinoCol) next[j] = 'ignorar'; });
      next[i] = destinoCol;
      return next;
    });

  /* ---------- Las filas y la revisión ---------- */
  const filas: FilaGasto[] = useMemo(() => {
    if (!archivo) return [];
    if (archivo.det) return filasDePlantillaGastos(archivo.raw, archivo.det);
    if (faltan.length > 0) return [];
    return filasDeExcel(archivo.datos, columnas, archivo.cabecera + 2);
  }, [archivo, columnas, faltan]);

  const ambiguo = useMemo(() => fechasAmbiguas(filas.map((f) => f.fecha)), [filas]);
  const diaPrimero = diaPrimeroElegido ?? (archivo?.det ? true : ambiguo ? diaPrimeroEnPais(me.data?.tenant?.country) : detectDayFirst(filas.map((f) => f.fecha)));

  // Lo ya guardado de esas fechas (para los repetidos) y del último año (la
  // categoría que suele llevar cada proveedor).
  const rango = useMemo(() => {
    const fechas = filas.map((f) => fechaDeCelda(f.fecha, diaPrimero)).filter((d): d is string => Boolean(d)).sort();
    if (fechas.length === 0) return null;
    const haceUnAnyo = new Date(Date.now() - 365 * 86_400_000).toISOString().slice(0, 10);
    const hoy = today();
    return { start: fechas[0] < haceUnAnyo ? fechas[0] : haceUnAnyo, end: fechas[fechas.length - 1] > hoy ? fechas[fechas.length - 1] : hoy };
  }, [filas, diaPrimero]);
  const guardados = useQuery({
    queryKey: ['expenses', 'para-importar', rango?.start, rango?.end],
    queryFn: () => api.listExpenses(rango!),
    enabled: Boolean(rango),
  });

  const revision = useMemo(() => {
    if (filas.length === 0) return null;
    const proveedores = suppliers.data ?? [];
    return revisarGastos(filas, {
      defaultLocationId: destino,
      locations: activos,
      suppliers: proveedores,
      subgrupos: settings.data?.expenseSubgroups,
      historico: categoriasPorProveedor(guardados.data ?? [], proveedores),
      guardados: guardados.data ?? [],
      hoy: today(),
      diaPrimero,
      settings: settings.data,
    }, correcciones);
  }, [filas, destino, activos, suppliers.data, settings.data, guardados.data, correcciones, diaPrimero]);

  /* ---------- Guardar ---------- */
  const nListos = revision?.listos.length ?? 0;
  const nRevisar = revision?.porRevisar.length ?? 0;
  const ocupado = importar.isPending;

  const guardar = () => {
    if (!revision || !archivo) return;
    const items = [
      ...revision.listos,
      ...revision.porRevisar.map((i) => ({ ...i, input: { ...i.input, porConfirmar: motivoPorConfirmar(i.dudas) } })),
    ];
    const permitirRepetidos = items
      .map((i, idx) => (correcciones[i.row]?.repetido === 'otra' || i.dudas.some((d) => d.kind === 'repetido') ? idx : -1))
      .filter((idx) => idx >= 0);
    importar.mutate(
      { items, createSuppliers: true, permitirRepetidos },
      {
        onSuccess: async ({ saved, repetidos, proveedoresCreados }) => {
          // «Mandarlo a Inversiones» y «Usar esta categoría siempre».
          let inversiones = 0;
          for (const i of revision.aInversiones) {
            const vida = suggestUsefulLife(i.input.concept);
            try {
              await api.createInvestment({
                locationId: i.input.locationId === SHARED_LOCATION_ID ? undefined : i.input.locationId,
                date: i.input.date,
                concept: i.input.concept,
                quantity: 1,
                unitPrice: i.input.netAmount,
                vatPct: i.input.vatPct,
                usefulLifeYears: vida?.maxYears ?? 5,
                fundingSource: 'propio',
                notes: i.supplierName ? `Proveedor: ${i.supplierName}` : undefined,
              });
              inversiones += 1;
            } catch {
              /* se dice abajo: las que no entraron, se añaden a mano */
            }
          }
          for (const c of revision.categoriaSiempre) {
            const id = c.supplierId ?? proveedoresCreados.get(normalizeText(c.supplierName));
            if (!id) continue;
            try {
              await api.updateSupplier(id, { name: c.supplierName, lastCategory: c.category, lastSubgroup: c.subgroup ?? null });
            } catch {
              /* no es grave: la próxima vez se pregunta otra vez */
            }
          }
          if (!archivo.det && archivo.cabecera >= 0) {
            saveMapping.mutate({
              headers: archivo.cabeceras,
              columns: Object.fromEntries(archivo.cabeceras.map((h, i) => [h, columnas[i] ?? 'ignorar'])),
              fileName: archivo.name,
            });
          }
          const listos = Math.max(0, saved - nRevisar);
          toast.success(
            [
              `Guardados ${formatNumber(listos)} ${listos === 1 ? 'gasto' : 'gastos'}.`,
              nRevisar > 0 ? `${nRevisar === 1 ? '1 queda' : `${nRevisar} quedan`} en «Por confirmar».` : '',
              repetidos > 0 ? `${repetidos === 1 ? '1 ya estaba' : `${repetidos} ya estaban`} en Alexia.` : '',
              inversiones > 0 ? `${inversiones === 1 ? '1 pasó' : `${inversiones} pasaron`} a Inversiones.` : '',
            ].filter(Boolean).join(' '),
          );
          onClose();
        },
        onError: (e) => {
          setFallo({ mensaje: e.message, fila: e instanceof ImportExpensesError ? e.desdeFila : undefined });
          toast.fail(e);
        },
      },
    );
  };

  const descargarPlantilla = () => descargarArchivoPublico(PLANTILLA_ARCHIVOS.gastos);
  const verEjemplo = () => descargarArchivoPublico(EXPENSE_EXAMPLE_FILE);
  const elegir = () => inputRef.current?.click();

  /*
   * «Importar 0 gastos» (14 oct 2026): si con estas columnas no entra
   * ninguna fila, no se deja solo el botón apagado: se dice por qué y se
   * vuelve a emparejar la fecha y el total.
   */
  const sinNada = Boolean(archivo && !archivo.det && faltan.length === 0 && revision && revision.listos.length + revision.porRevisar.length + revision.aInversiones.length === 0);
  const porQue = sinNada && revision ? porQueNoEntraNada(revision) : null;
  const preguntar: Array<'fecha' | 'total'> = faltan.length > 0 ? faltan : porQue ? [...(porQue.faltaFecha ? ['fecha' as const] : []), ...(porQue.faltaTotal ? ['total' as const] : [])] : [];
  const fase: 'elegir' | 'columnas' | 'revisar' = !archivo ? 'elegir' : preguntar.length > 0 ? 'columnas' : 'revisar';
  const muestra = (i: number) => (archivo?.datos ?? []).slice(0, 3).map((r) => String(r[i] ?? '').trim()).filter(Boolean).join(' · ');

  return (
    <Drawer
      open
      onClose={ocupado ? () => undefined : onClose}
      title={fase === 'revisar' ? 'Antes de guardar' : 'Importar gastos'}
      subtitle={
        fase === 'revisar' && archivo
          ? `${archivo.name} · ${formatNumber(filas.length)} ${filas.length === 1 ? 'gasto' : 'gastos'}`
          : 'Para pasar muchos gastos a la vez: los de meses anteriores, el Excel de tu contador o el de tu banco.'
      }
      width="44rem"
      footer={
        fase === 'revisar' ? (
          <div className="ing-excel__pie">
            <p>
              {nListos === 0
                ? 'Todavía no hay nada listo.'
                : `Se ${nListos === 1 ? 'guarda el que está bien' : `guardan los ${formatNumber(nListos)} que están bien`}.`}
              {nRevisar > 0 && ` ${nRevisar === 1 ? 'El que queda por revisar queda' : `Los ${nRevisar} por revisar quedan`} en «Por confirmar» hasta que ${nRevisar === 1 ? 'lo arregles' : 'los arregles'}.`}
            </p>
            <div className="ing-excel__botones">
              <button type="button" className="btn" disabled={ocupado || nListos + nRevisar === 0 || guardados.isLoading} onClick={guardar}>
                {ocupado ? 'Guardando…' : `Guardar ${formatNumber(nListos)} ${nListos === 1 ? 'gasto' : 'gastos'}`}
              </button>
              <button type="button" className="btn btn--ghost" onClick={onClose} disabled={ocupado}>Cancelar</button>
            </div>
          </div>
        ) : undefined
      }
    >
      <input
        ref={inputRef}
        type="file"
        accept=".csv,.xlsx,.xls,text/csv"
        className="visually-hidden"
        onChange={(event) => void onPickFile(event.target.files?.[0])}
      />
      {fallo && (
        <div className="form__section" role="alert">
          <p className="form__section-title">{fallo.mensaje}</p>
          <p className="form__section-hint">
            {fallo.fila ? `Ya están en Alexia los gastos de antes de la fila ${fallo.fila}. ` : ''}
            Si vuelves a subir el archivo, lo que lleva proveedor y nº de factura no se repite.
          </p>
        </div>
      )}
      {error && <p className="form__error" role="alert">{error}</p>}

      {fase === 'elegir' && (
        <div className="gasto-excel__elegir">
          <section className="gasto-excel__camino is-principal">
            <h3>Ya tengo mis gastos en un Excel</h3>
            <p>El tuyo, el de tu contador o lo que te da tu banco. Súbelo tal cual: no hace falta copiarlo a nuestra plantilla.</p>
            <p className="gasto-excel__ayuda">
              Alexia busca sola la fecha, el proveedor, el total y el IVA, se llamen como se llamen las columnas. Si alguna no la entiende,
              te pregunta cuál es cuál. <strong>Nada se guarda hasta que lo veas.</strong>
            </p>
            <button type="button" className="btn" onClick={elegir} disabled={leyendo}>{leyendo ? 'Leyendo el archivo…' : 'Subir mi Excel'}</button>
            <span className="gasto-excel__formatos">Excel, CSV o Google Sheets descargado como Excel.</span>
          </section>
          <section className="gasto-excel__camino">
            <h3>No los tengo en ningún lado</h3>
            <p>Usa nuestra plantilla: una fila por gasto.</p>
            <ol className="gasto-excel__pasos">
              <li>
                Descarga la plantilla de gastos.{' '}
                <button type="button" className="btn btn--ghost btn--sm" onClick={descargarPlantilla}>Descargar plantilla</button>{' '}
                <button type="button" className="btn btn--link btn--sm" onClick={verEjemplo}>Ver un ejemplo</button>
              </li>
              <li>Llénala. Solo hacen falta <strong>la fecha y el total</strong>. Lo demás (categoría, si ya lo pagaste…) lo rellena Alexia si lo dejas vacío.</li>
              <li>
                Súbela. Antes de guardar verás lo que va a entrar.{' '}
                <button type="button" className="btn btn--ghost btn--sm" onClick={elegir} disabled={leyendo}>Subir la plantilla</button>
              </li>
            </ol>
          </section>
          <p className="gasto-excel__foto">
            ¿Tienes las facturas en papel o en PDF? Mejor{' '}
            {onFoto ? <button type="button" className="btn btn--link btn--sm" onClick={onFoto}>súbelas como foto</button> : 'súbelas como foto'}: así también se
            actualizan los precios de tus ingredientes.
          </p>
        </div>
      )}

      {fase === 'columnas' && archivo && (
        <div className="stack gasto-excel__preguntar">
          {porQue ? (
            <>
              <p><strong>{porQue.motivo}</strong></p>
              <p className="gasto-excel__ayuda">
                Con las columnas que elegí no entra ningún gasto de «{archivo.name}»
                {revision && revision.noSeGuardan[0] ? `: en la fila ${revision.noSeGuardan[0].row}, «${revision.noSeGuardan[0].message}»` : ''}.
              </p>
            </>
          ) : (
            <p>
              No encuentro {faltan.length === 2 ? 'la fecha ni el total' : faltan[0] === 'fecha' ? 'la fecha' : 'el total'} en «{archivo.name}». ¿Cuál es?
            </p>
          )}
          {preguntar.map((campo) => (
            <div key={campo} className="field">
              <span className="field__label">{campo === 'fecha' ? 'La fecha está en' : 'El total (con IVA) está en'}</span>
              <SmartSelect
                ariaLabel={campo === 'fecha' ? 'Columna de la fecha' : 'Columna del total'}
                value=""
                placeholder="Elige la columna…"
                onChange={(v) => {
                  if (v === '') return;
                  cambiarColumna(Number(v), campo);
                  setFaltan((prev) => prev.filter((f) => f !== campo));
                }}
                options={[
                  { value: '', label: 'Elige la columna…' },
                  ...archivo.cabeceras.map((h, i) => ({ value: String(i), label: `${h}${muestra(i) ? ` (${muestra(i).slice(0, 40)})` : ''}` })),
                ]}
              />
            </div>
          ))}
          <button type="button" className="btn btn--ghost btn--sm" onClick={() => setArchivo(null)}>Subir otro archivo</button>
        </div>
      )}

      {fase === 'revisar' && archivo && revision && (
        <>
          {activos.length > 1 && (
            <div className="field gasto-excel__destino">
              <span className="field__label">Lo que no diga local va a</span>
              <SmartSelect
                ariaLabel="Local de los gastos sin local"
                value={destino}
                onChange={setDestino}
                options={[
                  ...activos.map((l) => ({ value: l.locationId, label: l.name })),
                  { value: SHARED_LOCATION_ID, label: 'Todos (se reparte)' },
                ]}
              />
            </div>
          )}
          <GastoExcelRevisar
            revision={revision}
            correcciones={correcciones}
            corregir={(row, patch) => setCorrecciones((prev) => ({ ...prev, [row]: { ...prev[row], ...patch } }))}
            locations={activos}
            subgrupos={settings.data?.expenseSubgroups}
            columnas={archivo.det ? undefined : { cabeceras: archivo.cabeceras, columnas, ignoradas, onCambiar: cambiarColumna }}
            fechas={ambiguo ? { diaPrimero, onCambiar: () => setDiaPrimeroElegido(!diaPrimero) } : undefined}
          />
          <p className="gasto-excel__foto">
            <button type="button" className="btn btn--link btn--sm" onClick={elegir} disabled={leyendo || ocupado}>Subir otro archivo</button>
            {' · '}El Excel no cambia los precios de tus ingredientes: para eso, sube las facturas como foto.
            {revision.total > 0 && ` Total con IVA: ${formatMoney(revision.total)}.`}
          </p>
        </>
      )}
    </Drawer>
  );
}
